<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Global Navigation -->
    <GlobalNavbarNext />

    <div class="flex min-h-[calc(100vh-80px)]">
      <!-- Floating Desktop Sidebar -->
      <aside class="hidden lg:block fixed left-16 top-32 z-40">
        <nav class="space-y-1">
          <router-link 
            v-for="item in navigationItems" 
            :key="item.name" 
            :to="item.to"
            :class="[
              $route.name === item.name
                ? 'bg-red-50 border-red-500 text-red-700'
                : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'group border-l-4 px-3 py-2 flex items-center text-sm font-medium'
            ]"
          >
            <component 
              :is="item.icon" 
              :class="[
                $route.name === item.name ? 'text-red-500' : 'text-gray-400 group-hover:text-gray-500',
                'flex-shrink-0 -ml-1 mr-3 h-6 w-6'
              ]"
            />
            <span class="truncate">{{ item.name }}</span>
          </router-link>
        </nav>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 lg:ml-64">
        <!-- Content Area -->
        <div class="p-4 lg:p-8 pb-20 lg:pb-8">
          <div class="max-w-6xl mx-auto">
            <!-- Router View -->
            <router-view />
          </div>
        </div>
      </main>
    </div>

    <!-- Mobile Bottom Navigation -->
    <nav class="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200/50 shadow-lg z-50">
      <div class="flex items-center justify-around px-1 py-2">
        <router-link 
          v-for="item in navigationItems" 
          :key="item.name" 
          :to="item.to"
          class="flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-200 flex-1 min-w-0"
          :class="{ 
            'text-red-600 bg-red-50': $route.path === item.to,
            'text-gray-500 hover:text-red-600 hover:bg-red-50/50': $route.path !== item.to
          }"
        >
          <component 
            :is="item.icon" 
            class="w-5 h-5 transition-transform flex-shrink-0"
            :class="{ 'scale-110 mb-0.5': $route.path === item.to }"
          />
          <span v-if="$route.path === item.to" class="text-[10px] font-medium truncate max-w-full">{{ item.name }}</span>
          <div 
            v-if="$route.path === item.to" 
            class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-red-600 rounded-full"
          ></div>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { hasFeature } from '@/brand'
import GlobalNavbarNext from '@/components/nav/GlobalNavbarNext.vue'
import {
  LayoutDashboard,
  PackageOpen,
  MapPin,
  Settings,
  Phone,
  Plus,
  Gift,
  MessagesSquare
} from 'lucide-vue-next'

const route = useRoute()
const authStore = useAuthStore()

// Navigation items for user dashboard
const navigationItems = computed(() => {
  const items = [
    {
      name: 'Prehľad',
      to: '/user',
      icon: LayoutDashboard
    },
    {
      name: 'Objednávky',
      to: '/user/orders',
      icon: PackageOpen
    },
    {
      name: 'Správy',
      to: '/user/messages',
      icon: MessagesSquare,
      feature: 'chat'
    },
    {
      name: 'Odmeny',
      to: '/user/rewards',
      icon: Gift,
      feature: 'rewards'
    },
    {
      name: 'Adresy',
      to: '/user/addresses',
      icon: MapPin
    },
    {
      name: 'Nastavenia',
      to: '/user/settings',
      icon: Settings
    }
  ]

  // Add marketing item for admins and salesmen
  if ((authStore.isAdmin || authStore.isSalesman) && hasFeature('telemarketing')) {
    items.splice(-1, 0, {
      name: 'Telemarketing',
      to: '/user/marketing',
      icon: Phone
    })
  }

  // Add create order item for admins only
  if (authStore.isAdmin) {
    items.splice(-1, 0, {
      name: 'Vytvoriť objednávku',
      to: '/user/create-order',
      icon: Plus
    })
  }

  return items.filter(item => !item.feature || hasFeature(item.feature))
})

// Computed properties
const pageTitle = computed(() => {
  const titles = {
    '/user': 'Prehľad účtu',
    '/user/orders': 'Moje objednávky',
    '/user/addresses': 'Doručovacie adresy',
    '/user/settings': 'Nastavenia účtu',
    '/user/marketing': 'Marketing - Telefónne čísla'
  }
  return titles[route.path] || 'Môj účet'
})

const pageSubtitle = computed(() => {
  const subtitles = {
    '/user': 'Prehľad vášho účtu a posledných aktivít',
    '/user/orders': 'História a stav vašich objednávok',
    '/user/addresses': 'Spravujte svoje doručovacie adresy',
    '/user/settings': 'Nastavenia účtu a preferencie',
    '/user/marketing': 'Spravujte sledované telefónne čísla'
  }
  return subtitles[route.path] || ''
})
</script>